import React, { useEffect, useRef, useState } from 'react';
import { Barcode, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { SearchSelect } from '@/components/po/SearchSelect';
import { prApi, prErr } from '@/services/prApi';

const LEVELS = [['category', 'Category'], ['design_name', 'Design Name'], ['shade', 'Shade'], ['size', 'Size']];
export const EMPTY_FG = { mapped_fg_name: '', design_name: '', fg_category: '', fg_shade: '', fg_size: '', fg_description: '', fg_tax_pct: null };
const selOf = (l) => ({ category: l.fg_category || '', design_name: l.mapped_fg_name ? l.design_name || '' : '', shade: l.fg_shade || '', size: l.fg_size || '' });
const parents = (i, s) => Object.fromEntries(LEVELS.slice(0, i).map(([f]) => [f, s[f]]));
const toFg = (s, it) => ({ mapped_fg_name: it.barcode, fg_category: s.category, design_name: s.design_name, fg_shade: s.shade, fg_size: s.size,
  fg_description: it.description || '', fg_tax_pct: it.tax_pct });

/** FG from the Rates Master: Category → Design Name → Shade → Size → Description gives the barcode (the FG Name); or type / scan the barcode. */
export const FgPicker = ({ line, onChange, idx, locked }) => {
  const [sel, setSel] = useState(() => selOf(line));
  const [opts, setOpts] = useState({});
  const [totals, setTotals] = useState({});
  const [busy, setBusy] = useState('');
  const [code, setCode] = useState('');
  const live = useRef(true);
  const seq = useRef({});
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);

  const fetchLevel = async (i, s, search = '') => {
    const field = i < 4 ? LEVELS[i][0] : 'barcode';
    const n = (seq.current[field] || 0) + 1;
    seq.current[field] = n;
    setBusy(field);
    try {
      const r = await prApi.fgOptions({ ...parents(i, s), ...(search ? { search } : {}) });
      const vals = i < 4 ? r.values : r.items;
      if (live.current && seq.current[field] === n) {
        setOpts((o) => ({ ...o, [field]: vals }));
        if (i < 4) setTotals((t) => ({ ...t, [field]: r.total, ...(search ? {} : { [`${field}_big`]: r.total > vals.length }) }));
      }
      return vals;
    } catch (e) { toast.error(prErr(e, 'Could not load the Rates Master')); return []; } finally { if (live.current && seq.current[field] === n) setBusy(''); }
  };
  const loadAll = (s) => {
    const upto = LEVELS.findIndex(([f]) => !s[f]);
    for (let i = 0; i <= (upto === -1 ? 4 : upto); i += 1) fetchLevel(i, s);
  };
  useEffect(() => { loadAll(sel); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (i, v, base) => {
    const s = { ...base, [LEVELS[i][0]]: v };
    LEVELS.slice(i + 1).forEach(([f]) => { s[f] = ''; });
    setSel(s);
    setOpts((o) => { const n = { ...o }; LEVELS.slice(i + 1).forEach(([f]) => delete n[f]); delete n.barcode; return n; });
    onChange(EMPTY_FG);
    const vals = await fetchLevel(i + 1, s);
    if (vals.length === 1) { if (i < 3) pick(i + 1, vals[0], s); else onChange(toFg(s, vals[0])); }
  };
  const byCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    try {
      const x = await prApi.fgLookup(code.trim());
      const s = { category: x.category, design_name: x.design_name, shade: x.shade, size: x.size };
      setSel(s); setCode(''); setOpts({}); loadAll(s);
      onChange(toFg(s, x));
    } catch (er) { toast.error(prErr(er, 'FG not found in the Rates Master')); }
  };
  const items = opts.barcode || [];
  return (
    <div className="space-y-2" data-testid={`pr-fg-picker-${idx}`}>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {LEVELS.map(([f, label], i) => (
          <div key={f}><label className="text-xs text-gray-500">{label}{i === 0 ? ' *' : ''}</label>
            <SearchSelect value={sel[f]} options={(opts[f] || []).map((v) => ({ value: v, label: v }))} onChange={(v) => pick(i, v, sel)}
              total={totals[f]} onSearch={totals[`${f}_big`] ? (qq) => fetchLevel(i, sel, qq) : undefined}
              disabled={locked || (i > 0 && !sel[LEVELS[i - 1][0]])} loading={busy === f} placeholder={i && !sel[LEVELS[i - 1][0]] ? `Pick ${LEVELS[i - 1][1].toLowerCase()} first` : `Select ${label.toLowerCase()}`}
              searchPlaceholder={`Search ${label.toLowerCase()}…`} testId={`pr-fg-${f.replace('_', '-')}-${idx}`} /></div>))}
        <div><label className="text-xs text-gray-500">Description / Barcode *</label>
          <SearchSelect value={line.mapped_fg_name} disabled={locked || !sel.size} loading={busy === 'barcode'}
            options={items.map((it) => ({ value: it.barcode, label: it.description || it.barcode, sub: `${it.barcode}${it.wsp ? ` · WSP ₹${it.wsp}` : ''}${it.tax_pct != null ? ` · GST ${it.tax_pct}%` : ''}` }))}
            onChange={(b) => onChange(toFg(sel, items.find((it) => it.barcode === b)))} placeholder={sel.size ? 'Select description' : 'Pick size first'}
            searchPlaceholder="Search description or barcode…" testId={`pr-fg-barcode-${idx}`} /></div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {line.mapped_fg_name
          ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800" data-testid={`pr-fg-selected-${idx}`}>
            <Barcode className="h-3.5 w-3.5" />FG <span className="font-mono font-semibold">{line.mapped_fg_name}</span>{line.fg_tax_pct != null && <span className="text-emerald-600">· GST {line.fg_tax_pct}%</span>}</span>
          : <span className="text-xs text-rose-600" data-testid={`pr-fg-missing-${idx}`}>FG is required — pick it above or type the barcode</span>}
        {!locked && <form onSubmit={byCode} className="ml-auto flex items-center gap-1">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="or type / scan FG barcode" className="h-8 w-48 text-xs" data-testid={`pr-fg-code-${idx}`} />
          <button type="submit" className="rounded-md border px-2 py-1 text-xs hover:bg-gray-50" data-testid={`pr-fg-code-go-${idx}`}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Find'}</button>
        </form>}
      </div>
    </div>
  );
};

export default FgPicker;
